import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Zap, Plus, Clock, CheckCircle2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import RewardsBanner from '../components/RewardsBanner';
import { formatCredits } from '../utils/credits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  queued: { color: '#8A8A9A', dot: 'status-dot-queued', label: 'Queued' },
  processing: { color: '#FF6B4A', dot: 'status-dot-processing', label: 'Processing' },
  completed: { color: '#34B76F', dot: 'status-dot-active', label: 'Completed' },
  failed: { color: '#EF4444', dot: 'status-dot-error', label: 'Failed' },
};

const SERVICE_LABELS = {
  ai_image: 'AI Image',
  '3d_render': '3D Render',
  data_viz: 'Data Viz',
  video_render: 'Video',
};

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const [statsRes, workersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/worker/status`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setWorkers(workersRes.data);
    } catch (err) { /* dashboard load errors are non-critical */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [statsRes, workersRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/worker/status`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setStats(statsRes.data);
        setWorkers(workersRes.data);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 text-[#8A8A9A] animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Credits', value: user?.unlimited_credits ? '∞' : (stats?.credits?.toFixed(1) || '0'), icon: Zap, accent: true },
    { label: 'Total Jobs', value: stats?.total_jobs || 0, icon: Clock },
    { label: 'Completed', value: stats?.completed || 0, icon: CheckCircle2 },
    { label: 'Failed', value: stats?.failed || 0, icon: AlertCircle },
  ];

  return (
    <div className="space-y-10" data-testid="dashboard-home">
      <RewardsBanner />

      <div className="flex items-end justify-between">
        <div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A9A] mb-2">Dashboard</p>
          <h1 className="font-display text-3xl font-light tracking-tight text-[#1A1A2E]" data-testid="dashboard-welcome">
            {user?.name?.split(' ')[0]}
          </h1>
        </div>
        <Link to="/dashboard/submit">
          <Button className="bg-[#FF6B4A] text-white font-medium rounded-md hover:bg-[#FF5533] text-xs h-9 px-5" data-testid="new-job-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Job
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-grid">
        {statCards.map((card) => (
          <div key={card.label} className="p-5 rounded-lg hover-lift" style={{ background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.04)' }} data-testid={`stat-card-${card.label.toLowerCase()}`}>
            <card.icon className={`w-4 h-4 mb-3 ${card.accent ? 'text-[#FF6B4A]' : 'text-[#8A8A9A]'}`} />
            <div className="font-display text-2xl font-light text-[#1A1A2E]">{card.value}</div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8A8A9A] mt-1">{card.label}</div>
          </div>
        ))}
      </div>

      {/* GPU Status — simple indicator */}
      {workers.length > 0 && (() => {
        const anyOnline = workers.some(w => {
          const lastSeen = w.last_heartbeat ? new Date(w.last_heartbeat) : null;
          return lastSeen && Math.round((Date.now() - lastSeen.getTime()) / 60000) < 5;
        });
        const processingCount = stats?.processing || 0;
        const queuedCount = stats?.queued || 0;
        const totalActive = processingCount + queuedCount;
        // Load color: 0 jobs = green, 1-3 = yellow, 4-7 = orange, 8+ = red
        const loadColor = !anyOnline ? '#8A8A9A' : totalActive === 0 ? '#34B76F' : totalActive <= 3 ? '#84CC16' : totalActive <= 7 ? '#F59E0B' : '#EF4444';
        const loadLabel = !anyOnline ? 'Offline' : totalActive === 0 ? 'Idle' : totalActive <= 3 ? 'Light Load' : totalActive <= 7 ? 'Moderate' : 'Heavy Load';
        return (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 shadow-sm" data-testid="gpu-status-indicator">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: loadColor, boxShadow: anyOnline ? `0 0 8px ${loadColor}60` : 'none' }} />
            <span className="text-xs font-medium text-[#1A1A2E]">GPU {anyOnline ? 'Online' : 'Offline'}</span>
            <span className="text-[10px] text-[#8A8A9A]">&middot;</span>
            <span className="text-[10px] font-medium" style={{ color: loadColor }}>{loadLabel}</span>
            {anyOnline && totalActive > 0 && <span className="text-[10px] text-[#8A8A9A]">{totalActive} job{totalActive !== 1 ? 's' : ''} active</span>}
          </div>
        );
      })()}

      {/* Active Jobs Banner */}
      {(stats?.processing > 0 || stats?.queued > 0) && (
        <div className="p-4 rounded-xl flex items-center justify-between bg-[#FFF0EA] border border-[#FF6B4A]/10">
          <div className="flex items-center gap-3">
            <span className="status-dot status-dot-processing" />
            <span className="text-xs text-[#4A4A5A]">
              {stats?.processing || 0} processing &middot; {stats?.queued || 0} queued
            </span>
          </div>
          <Link to="/dashboard/jobs" className="text-xs text-[#FF6B4A] hover:underline flex items-center gap-1">
            View <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}

      {/* Recent Jobs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm text-[#1A1A2E]">Recent</h2>
          <Link to="/dashboard/jobs" className="text-xs text-[#8A8A9A] hover:text-[#1A1A2E] flex items-center gap-1 transition-colors" data-testid="view-all-jobs-link">
            All jobs <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {stats?.recent_jobs?.length > 0 ? (
          <div className="space-y-1">
            {stats.recent_jobs.map((job) => {
              const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
              return (
                <Link
                  key={job.job_id}
                  to={`/dashboard/jobs/${job.job_id}`}
                  className="flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                  data-testid={`recent-job-${job.job_id}`}
                >
                  <span className={`status-dot ${sc.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1A1A2E] truncate">{job.title}</div>
                    <div className="text-[10px] text-[#8A8A9A] mt-0.5 font-mono uppercase tracking-wider">{SERVICE_LABELS[job.service_type] || job.service_type}</div>
                  </div>
                  <Badge className="text-[10px] rounded-md px-2 py-0.5 bg-transparent border" style={{ color: sc.color, borderColor: `${sc.color}30` }}>
                    {sc.label}
                  </Badge>
                  <ArrowRight className="w-3.5 h-3.5 text-[#8A8A9A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 rounded-lg" style={{ background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.04)' }}>
            <p className="text-[#8A8A9A] text-sm mb-5">No jobs yet.</p>
            <Link to="/dashboard/submit">
              <Button className="bg-[#FF6B4A] text-white font-medium rounded-md text-xs h-9 px-5" data-testid="empty-state-new-job">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Submit Job
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
